<template>
  <!-- Design: JobAdOffcanvas. Opened by any 'bv::toggle::collapse' with 'jobs-sidebar' (result
       lists, map, notebook, shared ?jobViewId= link). The ad text stays the job server's page. -->
  <div v-if="open" class="ts-job-panel-root">
    <div class="offcanvas-backdrop fade show ts-jobad-backdrop" aria-hidden="true" @click="close"></div>
    <aside
      ref="panel"
      class="offcanvas offcanvas-end ts-jobad ts-job-ad show"
      role="dialog"
      aria-modal="true"
      :aria-label="text.jobDetailLabel"
      tabindex="-1"
      @keydown.esc.stop="close"
    >
      <ts-panel-header
        class="ts-job-ad-topbar"
        :previous-label="text.previousJob"
        :next-label="text.nextJob"
        :previous-disabled="listIndex <= 0"
        :next-disabled="listIndex < 0 || listIndex >= jobListIds.length - 1"
        @close="close"
        @previous="goTo(listIndex - 1)"
        @next="goTo(listIndex + 1)"
      />

      <div class="ts-job-ad-tabrow d-flex align-items-center border-bottom">
        <nav class="ts-job-ad-tabs flex-grow-1" :aria-label="text.jobDetailLabel">
          <div class="nav nav-tabs border-0" role="tablist">
            <button
              v-for="(tab, index) in tabs"
              :id="`tsJobTab${index}`"
              :key="index"
              class="nav-link"
              :class="{ active: active === index }"
              type="button"
              role="tab"
              :aria-selected="String(active === index)"
              :aria-controls="`tsJobPane${index}`"
              @click="selectTab(index)"
            >
              <i class="bi" :class="tab.icon" aria-hidden="true"></i>
              <span v-html="tab.label"></span>
            </button>
          </div>
        </nav>
      </div>

      <div ref="body" class="offcanvas-body ts-job-ad-body ts-panel-scroll p-0">
        <!-- Stellenangebot: actions on the left, the ad itself on the right -->
        <div v-if="active === 0" id="tsJobPane0" class="ts-job-offer-layout" role="tabpanel" aria-labelledby="tsJobTab0">
          <aside class="ts-job-offer-sidebar">
            <div class="ts-panel-summary-inner ts-kp-scroll p-4">
              <div class="ts-job-offer-logo d-flex align-items-center justify-content-center fw-bold mx-auto overflow-hidden">
                <img v-if="companyLogo" class="img-fluid" :src="companyLogo" :alt="firmName" />
                <template v-else>{{ initials }}</template>
              </div>
              <div class="d-grid gap-2 mt-4">
                <button v-if="frameApply || applyHref" class="btn btn-primary" type="button" @click="apply">{{ text.apply }}</button>
                <button class="btn btn-dark" type="button" :aria-pressed="String(isSaved)" @click="toggleNotepad">
                  <i class="bi me-2" :class="isSaved ? 'bi-bookmark-check-fill' : 'bi-bookmark'" aria-hidden="true"></i>
                  {{ isSaved ? translations[10983] : text.rememberJob }}
                </button>
              </div>
              <!-- The job ID: a click shows the ad again (from the map, Wikipedia, a translation …) like the Stellenangebot tab -->
              <div class="ts-panel-info-id ts-panel-info-id--top mt-3">
                <i class="bi bi-hash ts-panel-info-icon" aria-hidden="true"></i>
                <button class="btn ts-job-id-link" type="button" @click="selectTab(0)">
                  <small class="ts-panel-info-text">{{ text.jobIdLabel }}: {{ jobId }}</small>
                </button>
                <button class="btn ts-copy-button" type="button" :aria-label="text.copyJobId" :title="text.copyJobId" @click="copyJobId">
                  <i class="bi" :class="copied ? 'bi-check-lg' : 'bi-copy'" aria-hidden="true"></i>
                </button>
              </div>
              <hr class="my-4" />
              <div class="ts-panel-info-list">
                <div class="ts-panel-info-row" :class="{ 'user-select-none blur-5': isFirmnameDisabled }">
                  <i class="bi bi-building ts-panel-info-icon" aria-hidden="true"></i>
                  <strong class="ts-panel-info-text" v-html="firmName"></strong>
                </div>
                <div v-if="location" class="ts-panel-info-row">
                  <i class="bi bi-geo-alt ts-panel-info-icon" aria-hidden="true"></i>
                  <span class="ts-panel-info-text">{{ location }}</span>
                </div>
                <div v-if="jobTypes" class="ts-panel-info-row">
                  <i class="bi bi-briefcase ts-panel-info-icon" aria-hidden="true"></i>
                  <span class="ts-panel-info-text">{{ jobTypes }}</span>
                </div>
                <div v-if="date" class="ts-panel-info-row">
                  <i class="bi bi-calendar ts-panel-info-icon" aria-hidden="true"></i>
                  <span class="ts-panel-info-text">{{ text.publishedOn }} {{ date }}</span>
                </div>
              </div>
              <hr class="my-4" />
              <div class="ts-job-eyebrow mb-2">{{ text.moreActions }}</div>
              <div class="ts-panel-info-list">
                <!-- The short link /jobs/<id> to share (sections/shareLinks.ts) -->
                <button class="btn ts-panel-info-row ts-panel-info-action" type="button" @click="copyLink">
                  <i class="bi ts-panel-info-icon" :class="linkCopied ? 'bi-check-lg' : 'bi-link-45deg'" aria-hidden="true"></i>
                  <span class="ts-panel-info-text">{{ copyLinkText }}</span>
                </button>
                <button
                  class="btn ts-panel-info-row ts-panel-info-action"
                  type="button"
                  :aria-pressed="String(mapShown)"
                  @click="showMap"
                >
                  <i class="bi bi-map ts-panel-info-icon" aria-hidden="true"></i>
                  <span class="ts-panel-info-text">{{ translations[9986] }}</span>
                </button>
                <button class="btn ts-panel-info-row ts-panel-info-action" type="button" @click="print">
                  <i class="bi bi-printer ts-panel-info-icon" aria-hidden="true"></i>
                  <span class="ts-panel-info-text">{{ translations[1983] }}</span>
                </button>
                <!-- Google translation of the ad: the language list of the ad page's own bar -->
                <ts-panel-language
                  v-if="barLanguages.length"
                  :languages="barLanguages"
                  :prompt="barLanguagePrompt"
                  :value="barLanguage"
                  @select="translateAd"
                />
                <!-- Wikipedia of the place, route, public transport: shown in the ad frame -->
                <button
                  v-for="action in barActions"
                  :key="action.key"
                  class="btn ts-panel-info-row ts-panel-info-action"
                  type="button"
                  :title="action.title || null"
                  @click="runBarAction(action)"
                >
                  <i class="bi ts-panel-info-icon" :class="action.icon" aria-hidden="true"></i>
                  <span class="ts-panel-info-text">{{ action.label }}</span>
                </button>
                <!-- Public transport when the partner switched it off in the ad page's bar (as in the old portal) -->
                <a
                  v-if="!hasBarTransport"
                  class="ts-panel-info-row ts-panel-info-action"
                  :href="transportUrl"
                  :title="translations[14834]"
                  target="_blank"
                  rel="noopener"
                >
                  <i class="bi bi-bus-front ts-panel-info-icon" aria-hidden="true"></i>
                  <span class="ts-panel-info-text">{{ transportLabel }}</span>
                </a>
                <a class="ts-panel-info-row ts-panel-info-action" :href="originalAdUrl" target="_blank" rel="noopener">
                  <i class="bi bi-box-arrow-up-right ts-panel-info-icon" aria-hidden="true"></i>
                  <span class="ts-panel-info-text">{{ text.originalAd }}</span>
                </a>
              </div>
            </div>
          </aside>
          <div class="ts-job-offer-content position-relative">
            <!-- "auf Karte anzeigen": the map takes the ad's place, like Wikipedia or the route -->
            <iframe
              v-if="mapShown"
              class="ts-job-ad-frame"
              :src="mapUrl"
              :title="`${translations[2102]}: ${mapAddress}`"
            ></iframe>
            <div v-if="iframeLoading && !mapShown" class="ts-loading d-flex align-items-center justify-content-center" role="status">
              <span class="spinner-border" aria-hidden="true"></span>
              <span class="visually-hidden">{{ text.loading }}</span>
            </div>
            <iframe
              v-show="!mapShown"
              id="job-iframe"
              :class="{ 'invisible': !adReady }"
              ref="adFrame"
              :key="adUrl"
              class="ts-job-ad-frame"
              :title="position || text.jobDetailLabel"
              :src="adUrl"
              allowfullscreen
              @load="onFrameLoad"
            ></iframe>
          </div>
        </div>

        <ts-job-company-pane
          v-if="active === 1"
          id="tsJobPane1"
          class="ts-job-pane"
          :key="`company-${jobId}`"
          role="tabpanel"
          aria-labelledby="tsJobTab1"
          :info="companyInfo"
          :contact="contact"
          :fallback-name="firmName"
          :fallback-location="location"
          :all-jobs-label="translations[13789]"
          :map-label="translations[2102]"
          @show-jobs="selectTab(2)"
        />

        <ts-job-company-jobs
          v-if="active === 2"
          id="tsJobPane2"
          class="ts-job-pane"
          :key="`jobs-${jobId}`"
          role="tabpanel"
          aria-labelledby="tsJobTab2"
          :company="firmName"
          @opened="selectTab(0)"
        />
      </div>
    </aside>
  </div>
</template>

<script lang="ts">
import { Component, Vue, Watch } from 'vue-property-decorator';
import { AppModule } from '@/store/modules/app';
import { copyLinkLabel, copyText, shareLink } from '@/sections/shareLinks';
import { UserModule } from '@/store/modules/user';
import { ProfessionModule } from '@/store/modules/profession';
import { ClustersModule } from '@/store/modules/clusters';
import { CompanyModule } from '@/store/modules/company';
import { NotebookModule } from '@/store/modules/notebook';
import { logEvent } from '@/api/EventLogger';
import {
  getJobContact, getJobInfo, getJobUrl, JobContact, JobInfo,
} from '@/api/Job';
import { getCompanyInfo } from '@/api/Company';
import { EVENT_JOB_CLICK } from '@/config/constant';
import { groupCurses } from '@/config/configuration';
import { isJobInNotepad, toggleJobNotepad } from '@/sections/jobs/notepad';
import { jobAddress, mapEmbedUrl } from '@/sections/jobs/address';
import TsPanelHeader from '@/components/Talentspace/detail/TsPanelHeader.vue';
import TsJobCompanyPane from '@/components/Talentspace/detail/TsJobCompanyPane.vue';
import TsJobCompanyJobs from '@/components/Talentspace/detail/TsJobCompanyJobs.vue';
import TsPanelLanguage from '@/components/Talentspace/detail/TsPanelLanguage.vue';
import {
  addFrameStyle, FRAME_ACTION_ICONS, FrameAction, frameActionsSelector, FrameLanguage, frameDocumentOf,
  readFrameActions, readFrameLanguages, runFrameAction, selectFrameLanguage,
} from '@/sections/frameBar';

const URL = {
  DEFAULT_ANALYSE_PRODUCTION_URL: '/jobnet',
  // (test14.job-server.net is gone; locally the live job server shows the ads)
  DEFAULT_ANALYSE_DEVELOPMENT_URL: 'https://job-server.net/jobnet',
};

const JOB_SERVER = 'https://job-server.net';

// The ad page (showangex.php) holds two frames: sh_top shows the ad, sh_bottom its button bar
const BAR_FRAME = 'iframe[name="sh_bottom"]';

// The ad page's own bar and menu; their buttons are in the left panel
const HIDE_BAR_CSS = '.frame_bottom, .hamburger { display: none !important; } .frame_main { height: 100% !important; }';

// Bar buttons that open something in the ad frame; the left panel shows them instead of the bar.
// Which ones exist depends on the partner (jobview_enable_* options, ÖBB in Austria …).
const BAR_ACTIONS_SELECTOR = frameActionsSelector(['icon-picture', 'icon-pin', 'icon-bus']);

@Component({
  components: {
    TsPanelHeader, TsJobCompanyPane, TsJobCompanyJobs, TsPanelLanguage,
  },
})
export default class TsJobPanel extends Vue {
  open = false;

  // 0 Stellenangebot · 1 company · 2 its other jobs
  active = 0;

  iframeLoading = true;

  // The ad page has its own "direkt bewerben" (the portal's application form); reachable when
  // the page is ours (production, same origin)
  frameApply = false;

  // Actions and translation languages taken over from the ad page's bar
  barActions: FrameAction[] = [];

  barLanguages: FrameLanguage[] = [];

  // The bar's "choose a language" text, shown until a language is picked
  barLanguagePrompt = '';

  barLanguage = '';

  copied = false;

  linkCopied = false;

  info: JobInfo = {};

  contact: JobContact | null = null;

  companyInfo: Record<string, any> = {};

  // The map is shown in place of the ad
  mapShown = false;

  // The ad page is shown once its own bar is hidden (watchAdFrame)
  adReady = false;

  adWatch = 0;

  // The ad on its source site (e.g. steirerjobs.at)
  sourceUrl = '';

  // Focus goes back here when the panel closes
  opener: HTMLElement | null = null;

  get translations() {
    return AppModule.translation;
  }

  get text() {
    return {
      jobDetailLabel: AppModule.translation[6223],
      previousJob: AppModule.translation[16562],
      nextJob: AppModule.translation[16563],
      apply: AppModule.translation[16569],
      rememberJob: AppModule.translation[10658],
      moreActions: AppModule.translation[5402],
      originalAd: AppModule.translation[16564],
      publishedOn: AppModule.translation[16618],
      jobIdLabel: AppModule.translation[15156],
      copyJobId: AppModule.translation[16565],
      loading: AppModule.translation[16555],
    };
  }

  get tabs() {
    return [
      { icon: 'bi-info-circle', label: this.translations[728] },
      { icon: 'bi-building', label: this.firmName },
      { icon: 'bi-list-ul', label: this.translations[13995] },
    ];
  }

  get isAuth() {
    return UserModule.isUserAuth;
  }

  get languageSid() {
    return AppModule.languageSid;
  }

  get jobId(): number {
    return ProfessionModule.jobId;
  }

  get jobListIds(): number[] {
    return (ProfessionModule.jobs.jobs || []).map((value: any) => value[1]);
  }

  get listIndex(): number {
    return this.jobListIds.indexOf(this.jobId);
  }

  // The row of the result list, when the job came from there
  get listRow(): any[] | null {
    return (ProfessionModule.jobs.jobs || []).find((value: any) => value[1] === this.jobId) || null;
  }

  get firmName(): string {
    return ProfessionModule.jobFirm || this.info.firmname || this.listRow?.[4] || '';
  }

  get position(): string {
    return this.info.position || this.listRow?.[2] || '';
  }

  get location(): string {
    return this.info.location || this.listRow?.[3] || '';
  }

  // "Ausbildung · Vollzeit": the type icons of the result list row (titles of the jn-icon spans)
  get jobTypes(): string {
    const icons = this.listRow?.[5];
    if (!icons) return '';
    const parsed = new DOMParser().parseFromString(String(icons), 'text/html');
    const titles = Array.from(parsed.querySelectorAll('[title]')).map((icon) => icon.getAttribute('title') || '');
    return titles.filter(Boolean).join(' · ');
  }

  get date(): string {
    return this.info.date || this.listRow?.[0] || '';
  }

  get initials(): string {
    const words = String(this.firmName).replace(/<[^>]*>/g, '').split(/\s+/).filter((word) => /^[A-Za-zÄÖÜäöü0-9]/.test(word));
    return words.slice(0, 2).map((word) => word[0].toUpperCase()).join('');
  }

  get companyLogo(): string {
    const url = this.companyInfo.logo_url;
    if (!url) return '';
    return url.startsWith('http') ? url : `${JOB_SERVER}${url}`;
  }

  // Where to apply: the ad's application page, else its e-mail address
  get applyHref(): string {
    if (this.contact?.apply_url) return this.contact.apply_url;
    if (this.contact?.email) return `mailto:${this.contact.email}?subject=${encodeURIComponent(this.position)}`;
    return '';
  }

  get isSaved(): boolean {
    return isJobInNotepad(this.jobId);
  }

  get isFirmnameDisabled() {
    return AppModule.settingPortal.not_logged_in_blur_employer_data && !this.isAuth;
  }

  // ─── the ad page of the job server ───────────────────────────────────────────

  get serverUrl() {
    return process.env.NODE_ENV === 'production'
      ? URL.DEFAULT_ANALYSE_PRODUCTION_URL
      : URL.DEFAULT_ANALYSE_DEVELOPMENT_URL;
  }

  get token() {
    return UserModule.accessTokenApplicant || UserModule.accessTokenConsultant || UserModule.accessTokenCompany || '';
  }

  get shareJobLink() {
    return window.btoa(encodeURI(`${window.location.origin}/jobs?lang=${this.$route.query.lang}&jobViewId=${this.jobId}&jobFirmName=${this.firmName}&jobShortview=${ProfessionModule.shortView}`));
  }

  // A course or map can hand its own page (urlIframe)
  get adUrl(): string {
    if (ClustersModule.urlIframe) return ClustersModule.urlIframe;
    return `${this.serverUrl}/ang/showangex.php?token=${this.token}&sid=${this.languageSid}&jobnews=1&idang=${this.jobId}&portal=1&share=${this.shareJobLink}`;
  }

  get mapAddress(): string {
    return jobAddress(this.companyInfo, this.contact, this.location);
  }

  get mapUrl(): string {
    return mapEmbedUrl(this.mapAddress);
  }

  // ─── public transport ────────────────────────────────────────────────────────

  get hasBarTransport(): boolean {
    return this.barActions.some((action) => action.icon === FRAME_ACTION_ICONS['icon-bus']);
  }

  // Austrian partners get the ÖBB timetable, the others Deutsche Bahn (like the ad page's bar)
  get isAustrianSite(): boolean {
    return AppModule.sid === 'jnjtsat';
  }

  // ÖBB: the job server's timetable page for the job's address. DB: bahn.de itself — the job
  // server's /jobnet/bahn returns an empty page for jobs (for courses it only shows bahn.de's start page)
  get transportUrl(): string {
    if (!this.isAustrianSite) return 'https://www.bahn.de/';
    return `${this.serverUrl}/oebb?sid=${this.languageSid}&job_id=${this.jobId}`;
  }

  get transportLabel(): string {
    const provider = this.isAustrianSite ? 'ÖBB' : 'DB';
    return `${provider}: ${this.translations[2822]}`;
  }

  // "Originalanzeige": the source site of the ad; our ad page only for ads without one
  get originalAdUrl(): string {
    if (/^https?:\/\//.test(this.sourceUrl)) return this.sourceUrl;
    return this.adUrl;
  }

  // ─── open / close ────────────────────────────────────────────────────────────

  onToggle(id: string) {
    if (id !== 'jobs-sidebar') return;
    if (this.open) this.close();
    else this.show();
  }

  show() {
    this.opener = document.activeElement as HTMLElement | null;
    this.open = true;
    this.active = 0;
    document.body.classList.add('ts-jobad-open');
    window.addEventListener('message', this.handleMessage, false);
    this.loadJob();
    this.$nextTick(() => (this.$refs.panel as HTMLElement | undefined)?.focus());
  }

  close() {
    this.open = false;
    document.body.classList.remove('ts-jobad-open');
    window.removeEventListener('message', this.handleMessage, false);
    this.opener?.focus?.();
  }

  // Another job (‹ ›, or one of the company's jobs): its ad from the top
  @Watch('jobId')
  onJobChange() {
    if (!this.open) return;
    this.loadJob();
  }

  goTo(index: number) {
    const id = this.jobListIds[index];
    if (id === undefined) return;
    const row = (ProfessionModule.jobs.jobs || [])[index] as any[];
    ProfessionModule.SetJobFirm(row?.[4] || '');
    ProfessionModule.SetJobShort(row?.[7] || '');
    ProfessionModule.UPDATE_JOB_ID(id);
  }

  // Without any address the company tab still has the company card
  showMap() {
    if (!this.mapAddress) {
      this.selectTab(1);
      return;
    }
    this.mapShown = true;
  }

  selectTab(index: number) {
    this.mapShown = false;
    // Back from the company tabs the ad frame is built anew
    if (index === 0 && this.active !== 0) this.watchAdFrame();
    // The ad tab again while open: back from a translation, route … to the ad itself
    if (index === 0 && this.active === 0) this.showAd();
    this.active = index;
    const body = this.$refs.body as HTMLElement | undefined;
    if (body) body.scrollTop = 0;
    if (index === 0) this.logJobView();
  }

  async loadJob() {
    this.active = 0;
    this.watchAdFrame();
    this.iframeLoading = true;
    this.resetFrameBar();
    this.info = {};
    this.contact = null;
    this.companyInfo = {};
    this.sourceUrl = '';
    this.mapShown = false;
    const { jobId } = this;
    this.logJobView();
    // The company profile belongs to this job
    CompanyModule.RESET_COMPANY_DETAIL_FILTER();
    CompanyModule.SetCompanyDetailFilter({ obj: 'idjob', value: jobId });
    const params = { sid: this.languageSid, job_id: jobId };
    const [info, contact, companyInfo, sourceUrl] = await Promise.all([
      getJobInfo(params).catch(() => ({})),
      getJobContact(params).catch(() => null),
      getCompanyInfo(params).catch(() => ({})),
      getJobUrl(params).catch(() => ''),
    ]);
    if (jobId !== this.jobId) return; // another job was chosen meanwhile
    this.info = info || {};
    this.contact = contact;
    this.companyInfo = companyInfo || {};
    this.sourceUrl = sourceUrl;
  }

  // ─── actions ─────────────────────────────────────────────────────────────────

  frameDocument(): Document | null {
    return frameDocumentOf(this.$refs.adFrame as HTMLIFrameElement | undefined);
  }

  // The ad page's button bar; reachable when the page is ours (production, same origin)
  barDocument(): Document | null {
    return frameDocumentOf(this.frameDocument()?.querySelector(BAR_FRAME) as HTMLIFrameElement | null);
  }

  onFrameLoad() {
    this.iframeLoading = false;
    this.readFrameBar();
    // (local development: the page is not ours, nothing to hide)
    this.adReady = true;
  }

  // The ad page's old bar must not flash while the page loads: the page stays invisible
  // (behind the spinner) until it exists and the bar's hiding style is in it
  watchAdFrame() {
    this.adReady = false;
    window.clearInterval(this.adWatch);
    const started = Date.now();
    // after the render: the frame of this job (the frame is built anew per job and tab)
    this.$nextTick(() => {
      const idang = `idang=${this.jobId}`;
      this.adWatch = window.setInterval(() => {
        const page = this.frameDocument();
        if (page?.head && page.URL.includes(idang)) {
          addFrameStyle(page, 'ts-hide-bar', HIDE_BAR_CSS);
          this.adReady = true;
        }
        if (this.adReady || Date.now() - started > 20000) window.clearInterval(this.adWatch);
      }, 20);
    });
  }

  resetFrameBar() {
    this.frameApply = false;
    this.barActions = [];
    this.barLanguages = [];
    this.barLanguagePrompt = '';
    this.barLanguage = '';
  }

  // Takes the bar's buttons over into the left panel and hides the bar
  readFrameBar() {
    this.resetFrameBar();
    const bar = this.barDocument();
    if (!bar?.querySelector('.button-container')) return;

    this.frameApply = Boolean(bar.querySelector('.direct-apply__j-btn'));

    this.barActions = readFrameActions(bar, BAR_ACTIONS_SELECTOR);

    const { prompt, languages } = readFrameLanguages(bar.querySelector('select.button-select'));
    this.barLanguagePrompt = prompt;
    this.barLanguages = languages;

    addFrameStyle(this.frameDocument(), 'ts-hide-bar', HIDE_BAR_CSS);
  }

  // The bar's own select does the translation (it loads /jobnet/translator into the ad frame)
  translateAd(value: string) {
    this.mapShown = false;
    selectFrameLanguage(this.barDocument()?.querySelector('select.button-select') as HTMLSelectElement | null, value);
    this.barLanguage = value;
  }

  // The bar's own link opens its page in the ad frame (or a new tab)
  runBarAction(action: FrameAction) {
    this.mapShown = false;
    runFrameAction(this.barDocument(), BAR_ACTIONS_SELECTOR, action);
  }

  // The bar's first button ("Stellen-ID") shows the ad again
  showAd() {
    this.mapShown = false;
    const bar = this.barDocument();
    const adLink = bar?.querySelector('.button-container a') as HTMLElement | null;
    if (!adLink) return;
    adLink.click();
    const select = bar?.querySelector('select.button-select') as HTMLSelectElement | null;
    if (select) select.value = '-';
    this.barLanguage = '';
  }

  // The portal's application form of the ad page; else the ad's application page or e-mail
  apply() {
    const button = this.barDocument()?.querySelector('.direct-apply__j-btn') as HTMLElement | null;
    if (button) {
      button.click();
      return;
    }
    if (this.applyHref) window.open(this.applyHref, '_blank', 'noopener');
  }

  async toggleNotepad() {
    if (!this.isAuth && !groupCurses(AppModule.sid)) this.$bvModal.show('not-auth-modal');
    await toggleJobNotepad({
      id: this.jobId,
      title: this.position,
      firmName: this.firmName,
      location: this.location,
      date: this.date,
      shortview: ProfessionModule.shortView,
    });
  }

  // The ad page prints itself when it is ours (production); otherwise it opens to be printed
  // The bar's own "drucken" makes the job server's PDF (in the chosen translation language);
  // without the bar the browser prints the ad page
  print() {
    const printLink = this.barDocument()?.querySelector('.button-container a.print_link') as HTMLAnchorElement | null;
    if (printLink) {
      window.open(printLink.href, '_blank', 'noopener');
      return;
    }
    const frame = this.$refs.adFrame as HTMLIFrameElement | undefined;
    try {
      frame?.contentWindow?.focus();
      frame?.contentWindow?.print();
    } catch {
      window.open(this.adUrl, '_blank', 'noopener');
    }
  }

  get copyLinkText() {
    return copyLinkLabel();
  }

  async copyLink() {
    await copyText(shareLink('jobs', this.jobId));
    this.linkCopied = true;
    window.setTimeout(() => { this.linkCopied = false; }, 1500);
  }

  async copyJobId() {
    const value = String(this.jobId);
    try {
      await navigator.clipboard.writeText(value);
    } catch {
      const field = document.createElement('textarea');
      field.value = value;
      field.setAttribute('readonly', '');
      field.style.position = 'fixed';
      field.style.opacity = '0';
      document.body.append(field);
      field.select();
      document.execCommand('copy');
      field.remove();
    }
    this.copied = true;
    window.setTimeout(() => { this.copied = false; }, 1500);
  }

  // Messages of the ad page: it asks to log in, or notes the job in the notebook
  async handleMessage(event: MessageEvent) {
    switch (event.data?.message) {
      case 'login_required':
        this.$bvModal.show('not-auth-modal');
        break;
      case 'note_job':
        if (UserModule.accessTokenConsultant || UserModule.accessTokenApplicant) {
          await NotebookModule.toogleNotepadItem({ type: 'job', item_id: event.data.id, isMarked: event.data.saved });
        }
        break;
      default:
        break;
    }
  }

  async logJobView() {
    if (!this.isAuth) return;
    await logEvent({ event_id: EVENT_JOB_CLICK, object_id: this.jobId });
  }

  created() {
    this.$root.$on('bv::toggle::collapse', this.onToggle);
  }

  beforeDestroy() {
    window.clearInterval(this.adWatch);
    this.$root.$off('bv::toggle::collapse', this.onToggle);
    window.removeEventListener('message', this.handleMessage, false);
    document.body.classList.remove('ts-jobad-open');
  }
}
</script>
